import { IconButton } from '@mui/material';
import { athleteDisplayName, AthletePreview } from '@strength/domain';
import { formatISODate } from '@strength/utils';
import { createColumnHelper } from '@tanstack/react-table';
import { MdArchive, MdNote, MdUnarchive } from '@teamworksdev/icons';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { DataTableWithContext } from '../../../components/table/data-table';
import {
  DataTableFilter,
  FilterOption,
} from '../../../components/table/data-table-context';
import { DataTablePlaceholder } from '../../../components/table/data-table-placeholder';
import { getLocale } from '../../../utils/i18n';
import { Note } from '../types';

type NotesTableProps = {
  notes: Note[];
  loading: boolean;
  type: 'Inbox' | 'Archived';
  athletes?: AthletePreview[];
  onRowClick: (note: Note) => void;
  onFilterChange: (filters: Record<string, any>) => void;
  onMarkAsResolved: (noteId: string, resolved: boolean) => void;
};

export function NotesTable({
  notes,
  loading,
  type,
  athletes = [],
  onRowClick,
  onFilterChange,
  onMarkAsResolved,
}: NotesTableProps) {
  const { t } = useTranslation();
  const count = notes.length;

  const columnHelper = createColumnHelper<Note>();

  const columns = [
    columnHelper.accessor('athleteFirstName', {
      header: () => t('name'),
      cell: ({ row }) =>
        athleteDisplayName({
          givenName: row.original.athleteFirstName,
          familyName: row.original.athleteLastName,
        }),
    }),
    columnHelper.accessor('createdAt', {
      header: () => t('dateAdded'),
      cell: ({ row }) =>
        row.original.createdAt
          ? formatISODate(row.original.createdAt, getLocale(), {
              month: 'short',
              day: '2-digit',
              year: 'numeric',
            })
          : '',
    }),
    columnHelper.accessor('teamName', {
      header: () => t('team'),
      cell: ({ row }) => row.original.teamName,
    }),
    columnHelper.accessor('note', {
      header: () => t('note'),
      size: 200,
      cell: ({ row }) => (
        <div className="whitespace-normal line-clamp-2">
          {row.original.note}
        </div>
      ),
    }),
    columnHelper.display({
      header: () => t('action'),
      id: 'actions',
      cell: ({ row }) => (
        <IconButton
          size="small"
          className="cursor-pointer"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onMarkAsResolved(row.original.alertId, !row.original.resolved);
          }}
        >
          {row.original.resolved ? <MdUnarchive /> : <MdArchive />}
        </IconButton>
      ),
    }),
  ];

  const athletesFilter: DataTableFilter<AthletePreview> = {
    id: 'athletes',
    title: t('athletes'),
    type: 'select',
    options: athletes.map<FilterOption<AthletePreview>>((athlete) => ({
      id: athlete.id,
      label: athleteDisplayName(athlete),
      value: athlete,
    })),
    multiselect: true,
    showSearch: true,
    showSelectAll: true,
  };

  const dateRangeFilter = {
    id: 'dateRange',
    type: 'dateRange' as const,
  };

  const filters = [athletesFilter, dateRangeFilter];

  return (
    <DataTableWithContext
      title={t('notesCount', { count })}
      columns={columns}
      data={notes}
      loading={loading}
      enablePagination
      initialPageSize={25}
      pageSizeOptions={[25, 50, 100]}
      onRowClick={onRowClick}
      enableFilters
      filters={filters}
      onFilterChange={onFilterChange}
      defaultFilterValues={{
        dateRange: {
          start: DateTime.now().minus({ month: 1 }).toISODate(),
          end: DateTime.now().toISODate(),
        },
      }}
      NoDataPlaceholder={() => (
        <DataTablePlaceholder
          icon={<MdNote size={24} />}
          title={type === 'Inbox' ? t('emptyInbox') : t('noArchivedNotes')}
          description={
            type === 'Inbox'
              ? t('emptyInboxDescription')
              : t('noArchivedNotesDescription')
          }
        />
      )}
    />
  );
}
